import { useCallback, useMemo } from 'react';
import { CommonActions } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { 
  RESIDENT_PATH_TO_SCREEN,
  IResidentUserState,
} from '@/constants/residents/residentNavigation';

// Use generic navigation type to support both RootStackParamList and ResidentMobileParamList
type AnyNavigation = {
  navigate: (screen: string, params?: any) => void;
  dispatch: (action: any) => void;
  goBack?: () => void;
};

// Screen metadata type
interface ResidentScreenMetadata {
  title: string;
  path: string;
}

// Screen metadata lookup
const RESIDENT_SCREEN_METADATA: Record<string, ResidentScreenMetadata> = {
  'Payments': { title: 'Payments', path: '/payments' },
  'ResidentPayments': { title: 'Payments', path: '/payments' },
  'LinkedAccounts': { title: 'Linked accounts', path: '/linked-accounts' },
  'ResidentLinkedAccounts': { title: 'Linked accounts', path: '/linked-accounts' },
  'Maintenance': { title: 'Maintenance', path: '/maintenance' },
  'ResidentMaintenance': { title: 'Maintenance', path: '/maintenance' },
  'Events': { title: 'Events', path: '/events' },
  'ResidentEvents': { title: 'Events', path: '/events' },
  'Marketplace': { title: 'Community marketplace', path: '/marketplace' },
  'ResidentMarketplace': { title: 'Community marketplace', path: '/marketplace' },
  'Resources': { title: 'My resources', path: '/resources' },
  'ResidentResources': { title: 'My resources', path: '/resources' },
  'WifiConnection': { title: 'CBX Wi-Fi', path: '/wifi' },
  'ResidentFaq': { title: 'FAQ', path: '/faq' },
  'Messages': { title: 'Property chat', path: '/property-chat' },
  'ResidentMessages': { title: 'Property chat', path: '/property-chat' },
  'BulletinsList': { title: 'Notifications', path: '/notifications' },
  'ResidentNotifications': { title: 'All notifications', path: '/notifications' },
  'Profile': { title: 'My account', path: '/my-account' },
  'ResidentProfile': { title: 'My account', path: '/my-account' },
  'ResidentAccountSettings': { title: 'Account', path: '/account-settings' },
  'CommunityBoard': { title: 'Newsfeed', path: '/newsfeed' },
  'ResidentCommunityBoard': { title: 'Newsfeed', path: '/newsfeed' },
  'Surveys': { title: 'Surveys', path: '/surveys' },
  'ResidentSurveys': { title: 'Surveys', path: '/surveys' },
  'Home': { title: 'Home', path: '/home' },
  'ResidentHome': { title: 'Home', path: '/home' },
  'ResidentPlanner': { title: 'Planner', path: '/planner' },
};

/**
 * Get screen metadata by screen name
 */
export const getResidentScreenMetadata = (screenName: string): ResidentScreenMetadata => {
  return RESIDENT_SCREEN_METADATA[screenName] || { title: screenName, path: '/' };
};

/**
 * Create navigation handler for resident screens (hamburger menu lateral navigation)
 *
 * Resets the root stack to Main with the target inner screen.
 * This ensures any root-level screens (e.g., TransactionHistory, MarketplaceProfile)
 * are popped, and the inner navigator is set to just the target screen.
 * Preserves Main's route key to avoid unnecessary re-mounts.
 */
export const createResidentNavigationHandler = (
  navigation?: AnyNavigation
) => {
  return (path: string) => {
    if (!navigation) return;

    const screenName = RESIDENT_PATH_TO_SCREEN[path];
    if (!screenName) {
      console.warn(`No screen mapping found for path: ${path}`);
      return;
    }

    // WifiConnection lives on the root stack, use navigate to bubble up
    if (screenName === 'WifiConnection') {
      navigation.navigate('WifiConnection' as any);
      return;
    }

    // Reset root stack to Main with the target inner screen.
    // This pops any root-level screens (TransactionHistory, MarketplaceProfile, etc.)
    // and resets the inner navigator to just the target screen.
    navigation.dispatch((state: any) => {
      const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
      return CommonActions.reset({
        index: 0,
        routes: [{
          key: mainRoute?.key,
          name: 'Main',
          state: {
            routes: [{ name: screenName }],
            index: 0,
          },
        }],
      });
    });
  };
};

/**
 * Custom hook for resident screen logic
 * Provides reusable logic for resident screens including navigation and sign out
 */
export const useResidentScreen = (
  currentScreenName?: string,
  navigation?: AnyNavigation
) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Build user state for navigation conditions
  const userState: IResidentUserState = useMemo(() => ({
    customerType: user?.customerType,
    isWifiEnabled: (() => {
      try {
        const flags = (user as any)?.property?.featureFlags?.flags || {};
        if (typeof flags.wifiEnabled === 'boolean') return !!flags.wifiEnabled;
        if (typeof flags['cbx-wifi-integration-enabled'] === 'boolean') return !!flags['cbx-wifi-integration-enabled'];
        return false;
      } catch {
        return false;
      }
    })(),
  }), [user]);

  // Get screen metadata
  const screenMetadata = useMemo(
    () => getResidentScreenMetadata(currentScreenName || 'Payments'),
    [currentScreenName]
  );

  // Memoized navigation handler
  const handleNavigate = useMemo(
    () => createResidentNavigationHandler(navigation),
    [navigation]
  );

  // Default logo press handler - reset to home screen
  const handleLogoPress = useCallback(() => {
    if (!navigation) return;
    navigation.dispatch((state: any) => {
      const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
      return CommonActions.reset({
        index: 0,
        routes: [{
          key: mainRoute?.key,
          name: 'Main',
          state: {
            routes: [{ name: 'ResidentHome' }],
            index: 0,
          },
        }],
      });
    });
  }, [navigation]);

  // Sign out handler with proper error handling
  const handleSignOut = useCallback(async () => {
    try {
      await dispatch(logout()).unwrap();
      // Navigation will be handled automatically by RootNavigator
      // when isAuthenticated becomes false
    } catch (error: any) {
      console.error('🚨 Resident sign out failed:', error);
      // Even if logout fails, RootNavigator will handle navigation based on auth state
    }
  }, [dispatch]);

  // Notification press handler - reset to notifications screen
  const handleNotificationPress = useCallback(() => {
    if (!navigation) return;
    navigation.dispatch((state: any) => {
      const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
      return CommonActions.reset({
        index: 0,
        routes: [{
          key: mainRoute?.key,
          name: 'Main',
          state: {
            routes: [{ name: 'ResidentNotifications' }],
            index: 0,
          },
        }],
      });
    });
  }, [navigation]);

  return {
    userState,
    screenMetadata,
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  };
};

